import { PriceVariantDTO, SupplierPreference } from '@api/generated/search-service';
import dayjs from 'dayjs';

/**
 * Tie-break při shodné ceně: garantovaná varianta má přednost před negarantovanou.
 * Funguje pro PriceVariantDTO i ProductTileResponseSupplier - obojí má isGaranted.
 */
export const guaranteedFirst = (a: { isGaranted: boolean }, b: { isGaranted: boolean }) => Number(b.isGaranted) - Number(a.isGaranted);

/**
 * Označení dodavatele u produktu rozhoduje před cenou (stejné pravidlo jako na BE
 * v `PriceVariantOrdering`): dokud existuje varianta se srdíčkem, vybírá se jen z nich,
 * negativně označená vyhraje jen tehdy, když žádná jiná není.
 */
export const preferenceRank = (preference?: SupplierPreference | null): number => {
  if (preference === SupplierPreference.Preferred) return 0;
  if (preference === SupplierPreference.NonPreferred) return 2;
  return 1;
};

export const findCheapestPriceVariant = (
  variants: PriceVariantDTO[],
  selectedSupplierIds: number[],
  /** supplierId -> označení dodavatele; chybějící záznam = neutrální. */
  preferenceBySupplierId: Record<number, SupplierPreference | undefined> = {},
): { variant: PriceVariantDTO | null; isExpired: boolean } => {
  const suppliersVariants = selectedSupplierIds.length > 0 ? variants.filter(t => selectedSupplierIds.includes(t.supplierId)) : variants;
  const date = dayjs();

  const rank = (v: PriceVariantDTO) => preferenceRank(preferenceBySupplierId[v.supplierId]);
  const bestFirst = (a: PriceVariantDTO, b: PriceVariantDTO) => rank(a) - rank(b) || a.currentPrice - b.currentPrice || guaranteedFirst(a, b);

  // Platnost se posuzuje výhradně podle validFrom/validTo. Období po garanci má vlastní
  // "extended" variantu, kterou vyrábí PriceVariantPreprocessJob, takže je pokryté tímhle
  // filtrem — fallback přes basicPriceValidTo byl zrušený návrh a nepatří sem zpátky.
  const validVariant = suppliersVariants
    .filter(t => dayjs(t.validFrom).isBefore(date) && dayjs(t.validTo).isAfter(date))
    .sort(bestFirst)
    ?.firstOrNull();

  if (validVariant) return { variant: validVariant, isExpired: false };

  const expiredVariant = [...suppliersVariants].sort(bestFirst)?.firstOrNull();

  return { variant: expiredVariant, isExpired: true };
};
